import { useEffect, useState } from 'react';

import { getPredictionWinnersAction } from '@/server/actions/prediction/getPredictionWinners.action';
import { T_PredictionWinnerModalType } from '@/ts/types/T_PredictionWinnerModalType';
import { T_WinnersData } from '@/ts/types/T_WinnersData';

interface I_UseWinnersDataProps {
  predictionAddress?: string | null;
  predictionId?: string | null;
  type: T_PredictionWinnerModalType;
}

export const useWinnersData = ({
  predictionAddress,
  predictionId,
  type
}: I_UseWinnersDataProps) => {
  const [primeData, setPrimeData] = useState<T_WinnersData | null>(null);
  const [flashData, setFlashData] = useState<T_WinnersData | null>(null);
  const [realPrice, setRealPrice] = useState<string>('');
  const [isLoadingPrime, setIsLoadingPrime] = useState(false);
  const [isLoadingFlash, setIsLoadingFlash] = useState(false);

  useEffect(() => {
    const fetchPrimeWinners = async () => {
      if (!predictionAddress || !predictionId || type !== 'winners') {
        setPrimeData(null);
        return;
      }

      setIsLoadingPrime(true);
      try {
        const response = await getPredictionWinnersAction(
          predictionAddress,
          predictionId,
          'prime',
          20,
          0
        );
        if (response?.data) {
          setPrimeData(response.data);
          setRealPrice(response.data.prediction?.resolvedPrice || '');
        }
      } catch (error) {
        console.error('Failed to fetch prime winners:', error);
        setPrimeData(null);
      } finally {
        setIsLoadingPrime(false);
      }
    };

    fetchPrimeWinners();
  }, [predictionAddress, predictionId, type]);

  useEffect(() => {
    const fetchFlashWinners = async () => {
      if (!predictionAddress || !predictionId || type !== 'winners') {
        setFlashData(null);
        return;
      }

      setIsLoadingFlash(true);
      try {
        const response = await getPredictionWinnersAction(
          predictionAddress,
          predictionId,
          'flash',
          20,
          0
        );
        if (response?.data) {
          setFlashData(response.data);
          setRealPrice(response.data.prediction?.resolvedPrice || '');
        }
      } catch (error) {
        console.error('Failed to fetch flash winners:', error);
        setFlashData(null);
      } finally {
        setIsLoadingFlash(false);
      }
    };

    fetchFlashWinners();
  }, [predictionAddress, predictionId, type]);

  const handleLoadMoreFlash = async () => {
    if (!predictionAddress || !predictionId || isLoadingFlash || !flashData)
      return;
    if (flashData.items.length >= flashData.total) return;

    setIsLoadingFlash(true);
    try {
      const currentOffset = flashData.items.length;
      const response = await getPredictionWinnersAction(
        predictionAddress,
        predictionId,
        'flash',
        20,
        currentOffset
      );
      if (response?.data) {
        setFlashData({
          items: [...flashData.items, ...response.data.items],
          total: response.data.total,
          limit: response.data.limit,
          offset: response.data.offset
        });
        setRealPrice(response.data.prediction?.resolvedPrice || '');
      }
    } catch (error) {
      console.error('Failed to load more flash winners:', error);
    } finally {
      setIsLoadingFlash(false);
    }
  };

  return {
    primeData,
    flashData,
    isLoadingPrime,
    isLoadingFlash,
    handleLoadMoreFlash,
    realPrice
  };
};
